import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import { useIntervalFn } from '@vueuse/core'
import { usePresentationsStore } from '@/stores/presentations'
import { useAudiobooksStore } from '@/stores/audiobooks'
import { usePopupStore } from '@/stores/popup'
import { useBrowserStore } from '@/stores/browser'
import type { TPresentationExport, TPresentationExportStatus } from '@/types/presentation'
import type { TAudiobookExport } from '@/types/audiobooks'

type TExportKind = 'png_zip' | 'pdf' | 'video' | 'acx_zip' | 'm4b_file'
type TExportSource = 'presentation' | 'audiobook'
type TExport = TPresentationExport | TAudiobookExport

type TTrackedExport = {
  source: TExportSource
  sourceId: number
  type: TExportKind
  title: string
  status: TPresentationExportStatus
  url: string | null
  error: string | null
}

const pollIntervalMs = 5000

const kindLabels: Record<TExportKind, string> = {
  png_zip: 'PNG',
  pdf: 'PDF',
  video: 'MP4',
  acx_zip: 'ACX package',
  m4b_file: 'm4b audiobook',
}

const sourceLabels: Record<TExportSource, string> = {
  presentation: 'Presentation',
  audiobook: 'Audiobook',
}

function isActiveStatus(status: TPresentationExportStatus): boolean {
  return status === 'PENDING' || status === 'PROCESSING'
}

function isAudiobookExport(exported: TExport): exported is TAudiobookExport {
  return 'audiobook_id' in exported
}

export const useExportsStore = defineStore('exports', () => {
  const presentations = usePresentationsStore()
  const audiobooks = useAudiobooksStore()
  const popup = usePopupStore()
  const browser = useBrowserStore()

  const tracked = ref<TTrackedExport[]>([])
  const hydrated = ref(false)

  const activeCount = computed(() => tracked.value.filter((item) => isActiveStatus(item.status)).length)
  const hasActive = computed(() => activeCount.value > 0)

  function labelFor(type: TExportKind): string {
    return kindLabels[type] ?? 'Export'
  }

  function fetchStatus(item: TTrackedExport): Promise<TExport | null> {
    if (item.source === 'audiobook') {
      if (item.type === 'm4b_file') return audiobooks.getM4bExportStatus(item.sourceId).then((res) => res?.export ?? null)

      return audiobooks.getAcxExportStatus(item.sourceId).then((res) => res?.export ?? null)
    }
    if (item.type === 'video') return presentations.getVideoExportStatus(item.sourceId)
    if (item.type === 'pdf') return presentations.getPdfExportStatus(item.sourceId)

    return presentations.getExportStatus(item.sourceId)
  }

  async function announceDone(item: TTrackedExport) {
    if (!item.url) return

    const subject = item.title ? `"${item.title}"` : `Your ${sourceLabels[item.source].toLowerCase()}`

    const open = await popup.confirm(`${sourceLabels[item.source]} export ready`, {
      content: `${subject} ${labelFor(item.type)} export is ready.`,
      labelConfirm: item.source === 'audiobook' ? 'Download' : 'Open',
      labelCancel: 'Later',
    })

    if (open) browser.openNewWindowTab(item.url)
  }

  async function announceFailed(item: TTrackedExport) {
    const subject = item.title ? `"${item.title}"` : `Your ${sourceLabels[item.source].toLowerCase()}`

    await popup.alert(`${sourceLabels[item.source]} export failed`, {
      content: item.error
        ? `${subject} ${labelFor(item.type)} export could not be generated: ${item.error}`
        : `${subject} ${labelFor(item.type)} export could not be generated. Please try again later.`,
    })
  }

  async function poll() {
    const active = tracked.value.filter((item) => isActiveStatus(item.status))

    if (!active.length) {
      pause()

      return
    }

    for (const item of active) {
      const result = await fetchStatus(item)

      if (!result) continue

      item.status = result.status
      item.url = result.url ?? null
      item.error = result.error ?? null

      if (result.status === 'DONE') await announceDone(item)
      if (result.status === 'FAILED' && item.source === 'audiobook') await announceFailed(item)
    }

    if (!tracked.value.some((item) => isActiveStatus(item.status))) pause()
  }

  const { pause, resume, isActive } = useIntervalFn(poll, pollIntervalMs, { immediate: false })

  function upsert(exported: TExport, title: string): TTrackedExport {
    const source: TExportSource = isAudiobookExport(exported) ? 'audiobook' : 'presentation'
    const sourceId = isAudiobookExport(exported) ? exported.audiobook_id : exported.presentation_id
    const type = exported.type as TExportKind
    const existing = tracked.value.find(
      (item) => item.source === source && item.sourceId === sourceId && item.type === type,
    )

    const next: TTrackedExport = {
      source,
      sourceId,
      type,
      title: title || existing?.title || '',
      status: exported.status,
      url: exported.url ?? null,
      error: exported.error ?? null,
    }

    if (existing) {
      Object.assign(existing, next)

      return existing
    }

    tracked.value.push(next)

    return next
  }

  function track(exported: TExport, title = '') {
    const item = upsert(exported, title)

    if (item.status === 'DONE') {
      if (item.url) browser.openNewWindowTab(item.url)

      return
    }

    if (isActiveStatus(item.status) && !isActive.value) resume()
  }

  async function hydrate() {
    if (hydrated.value) return

    hydrated.value = true

    const [presentationExports, audiobookExports] = await Promise.all([
      presentations.getActiveExports(),
      audiobooks.getActiveExports(),
    ])

    for (const exported of [...presentationExports, ...audiobookExports]) {
      upsert(exported, '')
    }

    if (tracked.value.some((item) => isActiveStatus(item.status)) && !isActive.value) resume()
  }

  return { tracked, activeCount, hasActive, track, hydrate }
})
